/*
 * Split landing — modal behaviour.
 *
 * The markup ships as a plain full-screen section so that it degrades to two working
 * links without JavaScript. Everything that makes it a dialog is added here, and only
 * while it is genuinely on screen:
 *
 *   - role="dialog" + aria-modal, so assistive tech treats it as its own context
 *   - focus moved onto the dialog itself, not onto a choice: a choice that receives focus
 *     on load matches :focus-visible and reveals its hover pill before anyone has touched
 *     the page. Tab from there reaches the first choice as usual
 *   - Tab trapped between the two choices and the close button
 *   - Escape and a visible close control, both of which settle on Musical Instrument
 *     (dark), the side the client wants a visitor to land on when they skip the choice
 *   - every other child of <body> made `inert`, so the header, the skip link and the
 *     rest of the page stop being tab stops behind an opaque overlay
 *   - focus handed to the header logo on close only when the visitor is using the
 *     keyboard; after a click it would just draw a focus ring nobody asked for
 *
 * Runs after audience.js (both deferred, and audience.js is first in the document), so
 * window.EminenceAudience is available by the time anything here can fire.
 */
(function () {
  const DISMISS_AUDIENCE = 'mi';

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  class SplitLanding extends HTMLElement {
    connectedCallback() {
      this.root = document.documentElement;
      this.inerted = [];
      this.isOpen = false;

      this.onKeydown = this.onKeydown.bind(this);
      this.onAudienceChange = this.onAudienceChange.bind(this);
      this.onDismiss = this.onDismiss.bind(this);
      this.onPointer = () => { this.usingKeyboard = false; };
      this.onKey = () => { this.usingKeyboard = true; };
      this.usingKeyboard = false;

      this.closeButton = this.querySelector('[data-audience-dismiss]');
      if (this.closeButton) this.closeButton.addEventListener('click', this.onDismiss);

      this.root.addEventListener('audience:change', this.onAudienceChange);

      /*
       * The overlay is shown by CSS alone — `html:not([data-audience])`. Reading the
       * attribute is therefore the same test, without depending on layout being ready.
       */
      if (!this.root.hasAttribute('data-audience')) this.open();
    }

    disconnectedCallback() {
      this.close({ apply: false, restoreFocus: false });
      this.root.removeEventListener('audience:change', this.onAudienceChange);
    }

    open() {
      if (this.isOpen) return;
      this.isOpen = true;

      this.setAttribute('role', 'dialog');
      this.setAttribute('aria-modal', 'true');
      if (this.closeButton) this.closeButton.hidden = false;

      this.setBackgroundInert(true);
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('pointerdown', this.onPointer, true);
      document.addEventListener('keydown', this.onKey, true);

      this.setAttribute('tabindex', '-1');
      this.focus({ preventScroll: true });
    }

    /*
     * `apply` decides whether closing also records a choice. Escape and the close button
     * settle on Musical Instrument — and record it, otherwise the overlay would come
     * straight back on the next page with no way past it. When the visitor picked a side
     * the choice is already recorded, so we only tear the dialog down.
     */
    close(options) {
      const settings = options || {};
      if (!this.isOpen) return;
      this.isOpen = false;

      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('pointerdown', this.onPointer, true);
      document.removeEventListener('keydown', this.onKey, true);
      this.removeAttribute('role');
      this.removeAttribute('aria-modal');
      this.removeAttribute('tabindex');
      if (this.closeButton) this.closeButton.hidden = true;
      this.setBackgroundInert(false);

      if (settings.apply) {
        const audience = window.EminenceAudience;
        if (audience) {
          audience.set(DISMISS_AUDIENCE);
        } else {
          this.root.setAttribute('data-audience', DISMISS_AUDIENCE);
        }
      }

      if (settings.restoreFocus === false) return;
      if (this.usingKeyboard) {
        this.moveFocusOut();
      } else if (document.activeElement && this.contains(document.activeElement)) {
        document.activeElement.blur();
      }
    }

    onDismiss(event) {
      event.preventDefault();
      this.close({ apply: true });
    }

    onAudienceChange() {
      /* A side was picked (or the default applied) — the overlay is hidden either way. */
      this.close({ apply: false });
    }

    onKeydown(event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        event.preventDefault();
        this.close({ apply: true });
        return;
      }

      if (event.key !== 'Tab') return;

      const stops = this.focusable();
      if (stops.length === 0) return;

      const first = stops[0];
      const last = stops[stops.length - 1];
      const active = document.activeElement;

      /* Focus starts on the dialog itself, which counts as "before the first stop". */
      if (active === this) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
        return;
      }

      if (event.shiftKey && (active === first || !this.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !this.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    }

    focusable() {
      return Array.prototype.filter.call(
        this.querySelectorAll(FOCUSABLE),
        (element) => !element.hidden && element.getClientRects().length > 0
      );
    }

    /*
     * Only direct children of <body> are touched, so one flag covers the skip link, the
     * header group, the other overlays and the page container without having to know
     * what any of them are. The section wrapper Shopify puts around this element is
     * skipped, otherwise the dialog would make itself inert too.
     */
    setBackgroundInert(on) {
      if (!('inert' in HTMLElement.prototype)) return;

      if (!on) {
        this.inerted.forEach((element) => {
          element.inert = false;
        });
        this.inerted = [];
        return;
      }

      this.inerted = Array.prototype.filter.call(
        document.body.children,
        (element) => !element.contains(this) && !element.inert
      );
      this.inerted.forEach((element) => {
        element.inert = true;
      });
    }

    /*
     * Sends focus somewhere sensible once the overlay is gone. The logo is the first
     * thing in the header and, for guests, the way back to this selector.
     */
    moveFocusOut() {
      const target =
        document.querySelector('.header__logo-link') || document.querySelector('.skip-link');

      if (target) {
        target.focus({ preventScroll: true });
      } else if (document.activeElement && this.contains(document.activeElement)) {
        document.activeElement.blur();
      }
    }
  }

  if (!customElements.get('split-landing')) {
    customElements.define('split-landing', SplitLanding);
  }
})();
